/**
 * One note sounded now, for a key that has just been played.
 *
 * Note entry from a MIDI keyboard wants an answer in the ear as well as on the page. A
 * player watching the staff can see that something was written; what they cannot see is
 * whether it was the octave they meant, and a keyboard with no sound of its own -- which is
 * most controllers -- leaves them nothing to tell it by.
 *
 * It is deliberately the same voice the player uses and deliberately not the player: an echo
 * has no schedule, no position and nothing to stop. It is struck and it decays.
 */

import type { AudioEngine } from "./port";
import { hertz, sound } from "./tone";
import type { ToneShape } from "./tone";

export interface EchoOptions extends Partial<ToneShape> {
  /** Loudness, 0 to 1. */
  readonly volume?: number;
  /** Seconds the note sounds for. Long enough to hear the pitch, short enough to play on. */
  readonly length?: number;
}

const DEFAULTS = {
  wave: "triangle",
  attack: 0.008,
  release: 0.06,
  volume: 0.3,
  length: 0.35,
} as const;

/** Sound one MIDI key, at once, through an engine that is already awake. */
export function echo(engine: AudioEngine, midi: number, options: EchoOptions = {}): void {
  const settings = { ...DEFAULTS, ...options };
  sound(engine, settings, hertz(midi), engine.currentTime, settings.length, settings.volume);
}
